import { useState } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Box, Slider, Typography } from '@mui/material'
import { ArrowForwardIos } from '@mui/icons-material'
import { Trans, useTranslation } from 'react-i18next'
import { Button } from './atoms/Button'
import {
  PENALTY_FIELDS, PENALTY_STEP_MAX, PENALTY_STEP_MIN, PenaltyBand, PenaltyField,
  penaltyBand, penaltyStepLabel, penaltyToStep, stepToPenalty, worstPenaltyBand,
} from '../utils/batopt-tuning'

/** Slider / value-label colour per band. Tweak here. */
const BAND_COLORS: Record<PenaltyBand, string> = {
  green: '#09E7A4',
  yellow: '#F4E356',
  red: '#F15B1A',
}

/** Warning box colours per (non-green) band. */
const WARNING_BOX: Record<Exclude<PenaltyBand, 'green'>, { border: string, background: string }> = {
  yellow: { border: '#F4E356', background: 'rgba(244, 227, 86, 0.08)' },
  red: { border: '#F15B1A', background: 'rgba(241, 91, 26, 0.10)' },
}

const MARKS = Array.from({ length: PENALTY_STEP_MAX - PENALTY_STEP_MIN + 1 }, (_, i) => ({ value: PENALTY_STEP_MIN + i }))

export interface BatteryTuningProps {
  /** Current wire values (int8 offsets) per penalty field. */
  values: Partial<Record<PenaltyField, number>>
  /** Called with the new wire value when a slider moves. */
  onChange: (field: PenaltyField, penalty: number) => void
  /** Reset every field to 0 (NEUTRAL). */
  onReset: () => void
}

/**
 * "Algorithm fine-tuning" accordion for the battery appliance settings:
 * the four battery optimizer state penalties as FAVOUR ↔ PREVENT sliders.
 * Collapsed by default.
 */
export const BatteryTuning = ({ values, onChange, onReset }: BatteryTuningProps): JSX.Element => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  const steps: Partial<Record<PenaltyField, number>> = {}
  for (const spec of PENALTY_FIELDS) {
    steps[spec.field] = penaltyToStep(spec.field, values[spec.field] ?? 0)
  }
  const worst = worstPenaltyBand(steps)

  return <Accordion disableGutters expanded={expanded} onChange={(_, open) => setExpanded(open)} sx={{ mt: 3 }} data-cy="accordion-Algorithm fine-tuning">
    <AccordionSummary expandIcon={<ArrowForwardIos data-cy="accordion-icon-Algorithm fine-tuning" />}>
      <Trans>Algorithm fine-tuning</Trans>
    </AccordionSummary>
    <AccordionDetails data-cy="box-Algorithm fine-tuning">
      <Typography variant="body2" sx={{ mx: 1, color: 'grey.300' }}>
        <Trans>With algorithm fine-tuning you can influence the optimization decisions. We recommend keeping the settings close to the default (0).</Trans>
      </Typography>

      {PENALTY_FIELDS.map(spec => {
        const step = steps[spec.field] ?? 0
        const band = penaltyBand(spec.field, step)
        const color = BAND_COLORS[band]
        return <Box key={spec.field} sx={{ mx: 1, mt: 3 }} data-cy={`box-${spec.title}`}>
          <Typography sx={{ fontWeight: 500 }}>{t(spec.title)}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '3.5rem 1fr 3.5rem', alignItems: 'center', columnGap: 1.5, mt: 3.5 }}>
            <Typography variant="caption" sx={{ color: 'grey.400', textAlign: 'right', letterSpacing: '0.05em' }}>{t('FAVOUR')}</Typography>
            <Slider
              data-cy={`slider-${spec.title}`}
              size="small"
              min={PENALTY_STEP_MIN}
              max={PENALTY_STEP_MAX}
              step={1}
              marks={MARKS}
              value={step}
              track={false}
              valueLabelDisplay="on"
              valueLabelFormat={(s) => penaltyStepLabel(s, t)}
              onChange={(_, s) => onChange(spec.field, stepToPenalty(spec.field, s as number))}
              sx={{
                '& .MuiSlider-rail': { color, opacity: 0.6 },
                // Explicit px strings: a bare `width: 1` means 100 % in sx.
                '& .MuiSlider-mark': { backgroundColor: color, opacity: 0.6, height: '4px', width: '1px' },
                '& .MuiSlider-mark[data-index="10"]': { height: '10px', opacity: 1 },
                '& .MuiSlider-thumb': { color: '#FFF', borderColor: color, borderWidth: 2, borderStyle: 'solid' },
                '& .MuiSlider-valueLabel': { backgroundColor: color, color: '#000', fontWeight: 600, fontSize: '0.75rem' },
              }}
            />
            <Typography variant="caption" sx={{ color: 'grey.400', letterSpacing: '0.05em' }}>{t('PREVENT')}</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'grey.400', mt: 1 }}>{t(spec.description)}</Typography>
        </Box>
      })}

      {worst !== 'green' && <Box
        data-cy="box-Tuning warning"
        sx={{
          mx: 1,
          mt: 3,
          p: 1.5,
          borderRadius: '0.625rem',
          border: `1px solid ${WARNING_BOX[worst].border}`,
          backgroundColor: WARNING_BOX[worst].background,
        }}
      >
        <Typography variant="body2">
          <Trans>The algorithm's default settings (NEUTRAL) are the result of extensive development and simulation work and produce the best real savings in almost every case. A significant change to these settings will most likely reduce, not improve, your savings. These settings are only worthwhile in special cases.</Trans>
        </Typography>
      </Box>}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mx: 1 }}>
        <Button variant="outlined" size="small" onClick={onReset}><Trans>Reset to defaults</Trans></Button>
      </Box>
    </AccordionDetails>
  </Accordion>
}
